/**
 * Get Roles Use Case
 *
 * Fetches all roles for the organization.
 * Currently a simple pass-through, but provides a place for future enhancements:
 * - User permission filtering
 * - Role visibility rules
 * - Sorting/filtering logic
 * - Logging and auditing
 *
 * @module domains/personnel/application/use-cases/GetRolesUseCase
 */

import type { IRoleApiPort } from '@personnel/application/ports/IRoleApiPort';
import type { Role } from '@personnel/domain/entities/Role';

/**
 * Creates a use case to fetch all roles via the API port
 *
 * @param props - Dependencies
 * @param props.apiPort - Role API port implementation
 * @returns Use case with execute method
 *
 * @example
 * const apiPort = createRoleHttpClient({ httpClient: connectApi });
 * const useCase = getRolesUseCase({ apiPort });
 * const roles = await useCase.execute();
 */
export const getRolesUseCase = (props: { apiPort: IRoleApiPort }) => {
  const { apiPort } = props;

  /**
   * Executes the roles fetching operation
   *
   * @returns Promise resolving to array of Role entities
   * @throws Error if the repository call fails
   *
   * @example
   * const roles = await execute();
   * console.log(roles.length); // 5
   */
  const execute = async (): Promise<Role[]> => {
    // Future enhancements could go here:
    // - Filter by user permissions
    // - Sort by name/creation date
    // - Apply visibility rules
    // - Log access

    const roles = await apiPort.getRoles();

    return roles;
  };

  return {
    execute,
  };
};
